import type { TransitionRootProps, DialogProps } from '@headlessui/react';
import { Dialog, DialogPanel, DialogTitle, DialogBackdrop } from '@headlessui/react';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import CloseButton from './CloseButton';
import Button from '@/components/common/Button';
import { useZIndex } from '@/utils/zIndexManager';

export interface ModalProps {
    // should the modal be shown or not
    open: boolean;
    // function to be called when the modal should be closed
    onClose: () => void;
    // title of the modal
    title?: string;
    titleRight?: ReactNode;
    // subtitle of the modal (optional text below title)
    subtitle?: string;
    // content of the modal
    children: ReactNode;
    // text for the ok button
    okText?: string;
    // color for the ok button (defaults to 'teal')
    okColor?: 'teal' | 'default' | 'primary' | 'secondary' | 'danger';
    // should the ok button be disabled
    okDisabled?: boolean;
    // text for the cancel button
    cancelText?: string;
    // function to be called when the ok button is clicked
    onOk?: (e: React.MouseEvent<HTMLButtonElement> | KeyboardEvent) => void;
    // function to be called when the cancel button is clicked
    onCancel?: (e: React.MouseEvent<HTMLButtonElement> | KeyboardEvent) => void;
    // additional class name for the modal
    className?: string;
    // should the modal have no action
    noAction?: boolean;
    // data-cy attribute for testing
    dataCy?: string;
    // footer of the modal
    footer?: ReactNode;
    // transition props
    onAfterLeave?: TransitionRootProps['afterLeave'];
    showCloseButton?: boolean;
    largeTitle?: boolean;
    /**
     * Custom class name for the subtitle
     */
    subtitleClassName?: string;
}

const Modal = ({
    open,
    onClose,
    title,
    titleRight,
    subtitle,
    children,
    okText,
    okColor = 'teal',
    okDisabled,
    cancelText,
    onOk,
    onCancel,
    className,
    noAction = false,
    dataCy,
    footer,
    onAfterLeave,
    showCloseButton = true,
    largeTitle = false,
    subtitleClassName,
}: ModalProps) => {
    const { t } = useTranslation('common');

    const zIndex = useZIndex(open);

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (open && event.key === 'Enter' && !(event.shiftKey || event.ctrlKey || event.altKey || event.metaKey)) {
                onOk?.(event);
                event.preventDefault();
            }
        };

        document.addEventListener('keypress', handleKeyPress);

        return () => {
            document.removeEventListener('keypress', handleKeyPress);
        };
    }, [open, onOk]);

    const onTransitionEnd = useCallback<NonNullable<DialogProps['onTransitionEnd']>>(() => {
        if (!open && onAfterLeave) {
            onAfterLeave();
        }
    }, [onAfterLeave, open]);

    return (
        <Dialog
            className="relative"
            data-cy={dataCy}
            onClose={onClose}
            onTransitionEnd={onTransitionEnd}
            open={open}
            style={{ zIndex }}
        >
            <DialogBackdrop
                className={clsx(
                    'fixed inset-0 bg-gray-500/75',
                    // backdrop transition
                    'transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in'
                )}
                transition
            />

            {/* Modal container to place it */}
            <div className="fixed inset-0 w-screen overflow-y-auto" style={{ zIndex }}>
                <div className="flex min-h-full items-center justify-center p-4 text-center">
                    {/* Modal */}
                    <DialogPanel
                        className={clsx(
                            // modal panel style
                            'relative w-full transform rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl sm:my-8 sm:w-auto sm:p-6',
                            // modal panel transition
                            'transition-all data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in',
                            className
                        )}
                        transition
                    >
                        {title && (
                            <div className={clsx('mt-0')}>
                                <div className="flex items-center justify-between">
                                    <div className="flex flex-col gap-1">
                                        <DialogTitle
                                            as="h3"
                                            className={clsx(
                                                'break-words',
                                                largeTitle
                                                    ? 'text-xl leading-8 font-bold text-neutral-900 lg:text-2xl'
                                                    : 'text-base leading-6 font-semibold text-neutral-900'
                                            )}
                                        >
                                            {title}
                                        </DialogTitle>
                                        {subtitle && (
                                            <p
                                                className={clsx(
                                                    'leading-6 font-medium text-neutral-500',
                                                    subtitleClassName
                                                )}
                                            >
                                                {subtitle}
                                            </p>
                                        )}
                                    </div>
                                    {titleRight && <div>{titleRight}</div>}
                                    {showCloseButton && (
                                        <div className="relative h-6 w-6 overflow-hidden">
                                            <CloseButton onClick={onClose} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="mt-5">{children}</div>
                        {footer && <div className="mt-5">{footer}</div>}
                        {!noAction && !footer && (
                            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                <Button className="ml-3" color={okColor} disabled={okDisabled} onClick={onOk}>
                                    {okText || t('common:modal.ok')}
                                </Button>
                                <Button className="ml-3" color="secondary" onClick={onCancel || onClose}>
                                    {cancelText || t('common:modal.cancel')}
                                </Button>
                            </div>
                        )}
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
};

export default Modal;
